<template>
  <div class="listing-search-filter-row">
    <div class="container">
      <div class="__filter-item-container">
        <!-- Search input field -->
        <ElementsCityboundSearch v-model="mapBounds" class="__input-search" label="Location" @update:model-value="updateRouteQuery()" />
        <!--        <ElementsInputsText v-model="searchKeyword" class="__input-search" label="Search" placeholder="Property name, region, suburb or builder..." :prepend-icon="searchIcon" @keyup="debouncedSearchKeyword()" />-->

        <!-- Property Type -->
        <ElementsInputsMultipleSelect v-if="isHidden('property-type')" v-model="selectedListingTypes" class="__filter-dropdown __property-type" label="Property Type" :items="propertyTypeItems" @update:model-value="updateRouteQuery()" />

        <!-- Bedrooms -->
        <ElementsInputsDropdown v-if="isHidden('bedrooms')" v-model="bedrooms" class="__filter-dropdown" label="Bedrooms" :items="items" @update:model-value="updateRouteQuery()" />

        <!-- Bathrooms -->
        <ElementsInputsDropdown v-if="isHidden('bathrooms')" v-model="bathrooms" class="__filter-dropdown" label="Bathrooms" :items="items" @update:model-value="updateRouteQuery()" />

        <!-- Bathrooms -->
        <ElementsInputsDropdown v-if="isHidden('livingAreas')" v-model="livingAreas" class="__filter-dropdown" label="Living Areas" :items="items" @update:model-value="updateRouteQuery()" />

        <!-- Parking -->
        <ElementsInputsDropdown v-if="isHidden('parking')" v-model="garageSpaces" class="__filter-dropdown" label="Parking" :items="items" @update:model-value="updateRouteQuery()" />

        <!-- Budget -->
        <div v-if="isHidden('budget')" v-click-outside="() => toggleBudgetFilterOpen(true)" class="__filter-budget" :class="{'--opened': isBudgetFilterOpen}" @click="toggleBudgetFilterOpen()">
          <span class="__label">Budget</span>
          <div class="__budget-inner">
            <span class="__value">{{ budgetRange[0] }} - {{ budgetRange[1] }}M</span>
            <img v-lazy-load  data-src="~/assets/images/icons/down-arrow.webp" alt="Down arrow" width="14px" height="7px">
          </div>
          <div class="__filter-range-slider" :class="{'--opened': isBudgetFilterOpen}">
            <ElementsInputsRangeSlider v-model="budgetRange" class="__budget-range" :min="0" :max="10" :step="0.2" suffix=" M" label="Budget:" @update:model-value="updateRouteQuery()" />
          </div>
        </div>

        <!-- Other search popup trigger -->
        <ElementsButtonsIcon v-if="hiddenFilters.length < 5" class="__btn-slider-icon" :icon-src="sliderIcon" :is-active="isOtherSearchPopupOpen" @click="triggerOtherSearchPopup()" />
        <BlocksOtherSearchPopup v-model="otherSearchPopupData" :is-open="isOtherSearchPopupOpen" :hidden-filters="hiddenFilters" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {Ref} from 'vue';
import {DropdownItem} from '~/@types/common';
import sliderIcon from '~/assets/images/icons/slider.webp';
// import searchIcon from '~/assets/images/icons/search-dark.webp';
import vClickOutside from '~/composables/clickOutside';
// import {debounce} from 'perfect-debounce';
import {propertyTypes} from '~/composables/propertyTypes';
import {ListingType} from '~/@types/listing';
import ListingSearchQueryHandler, {ListingSearchRouteQuerySetQuery} from '~/composables/listingSearchQuery';

// DATA Definitions
//----------------------------------------------------------------------------------------------------------------------
// const debouncedSearchKeyword = debounce(updateRouteQuery, 300);
const route = useRoute();
const searchKeyword: Ref<string> = ref('');
const mapBounds: Ref<string> = ref('');
const selectedListingTypes: Ref<ListingType[]> = ref([]);
const bedrooms: Ref<string> = ref('');
const bathrooms: Ref<string> = ref('');
const garageSpaces: Ref<string> = ref('');
const livingAreas: Ref<string> = ref('');
const landArea: Ref<string> = ref('');
const budgetRange: Ref<[number, number]> = ref([0, 10] as [number, number]);
const isOtherSearchPopupOpen: Ref<boolean> = ref(false);
const isBudgetFilterOpen: Ref<boolean> = ref(false);
const otherSearchPopupData: Ref<ListingSearchRouteQuerySetQuery> = ref({});
const hiddenFilters: Ref<string[]> = ref(['property-type', 'bedrooms', 'bathrooms', 'parking', 'budget', 'livingAreas']);
const propertyTypeItems: Ref<DropdownItem[]> = ref(propertyTypes);
const hasMounted: Ref<boolean> = ref(false);
const items: Ref<DropdownItem[]> = ref([
  {label: 'any', value: ''},
  {label: '1', value: '1'},
  {label: '2', value: '2'},
  {label: '3', value: '3'},
  {label: '4', value: '4'},
  {label: '4+', value: '>4'},
]);

updateFilterValues();

/**
 * Component MOUNTED!
 */
onMounted(() => {
  whenResized();
  window.addEventListener('resize', whenResized);
  nextTick(() => {
    hasMounted.value = true;
  });
});

/**
 * @_WATCHER - route.query
 */
watch(() => route.query, () => {
  // Update the filter values based on the new route query
  updateFilterValues();
});

/**
 * @_WATCHER - otherSearchPopupData
 */
watch(() => otherSearchPopupData.value, () => {
  // Update the filter values based on the new route query
  updateRouteQuery();
});


/**
 * Update listing filters based on filter values
 */
function updateRouteQuery () {
  // If yet not mounted, do not change route query based on filter values
  if (!hasMounted.value) return;
  ListingSearchQueryHandler.set({
    mapBounds: mapBounds.value,
    listingTypes: selectedListingTypes.value,
    bedrooms: bedrooms.value,
    bathrooms: bathrooms.value,
    garageSpaces: garageSpaces.value,
    livingAreas: livingAreas.value,
    landArea: landArea.value,
    budget: budgetRange.value,
    ...otherSearchPopupData.value,
  });
}

/**
 * Update the filter values based on the route query values
 */
function updateFilterValues () {
  const routeQuery = ListingSearchQueryHandler.get();
  searchKeyword.value = routeQuery.keyword;
  selectedListingTypes.value = Array.isArray(routeQuery.listingTypes) ? routeQuery.listingTypes : [];
  bedrooms.value = routeQuery.bedrooms;
  bathrooms.value = routeQuery.bathrooms;
  garageSpaces.value = routeQuery.garageSpaces;
  livingAreas.value = routeQuery.livingAreas;
  landArea.value = routeQuery.landArea;
  budgetRange.value = routeQuery.budget;
}

/**
 * Toggle Budget Slider
 */
function toggleBudgetFilterOpen(forcedToClose = false) {
  if (forcedToClose) {
    isBudgetFilterOpen.value = false;
  } else {
    isBudgetFilterOpen.value = !isBudgetFilterOpen.value;
  }

  if (isBudgetFilterOpen.value) {
    isOtherSearchPopupOpen.value = false;
  }
}

/**
 * Trigger the other search item popup
 */
function triggerOtherSearchPopup(forcedToClose = false) {
  if (forcedToClose) {
    isOtherSearchPopupOpen.value = false;
  } else {
    isOtherSearchPopupOpen.value = !isOtherSearchPopupOpen.value;
  }

  if (isOtherSearchPopupOpen.value) {
    isBudgetFilterOpen.value = false;
  }
}

/**
 * Check is the given filter hidden on main row
 *
 * @param filterName
 */
function isHidden(filterName: string) {
  return hiddenFilters.value && hiddenFilters.value.includes(filterName);
}

/**
 * Set the hidden events when resize the window
 */
function whenResized() {
  const screenWidth = window.innerWidth;
  switch (true) {
    case (screenWidth > 991):
      hiddenFilters.value = ['property-type', 'bedrooms', 'bathrooms', 'parking', 'budget', 'livingAreas'];
      break;
    case (screenWidth > 891):
      hiddenFilters.value = ['property-type', 'bedrooms', 'bathrooms', 'budget'];
      break;
    case (screenWidth > 791):
      hiddenFilters.value = ['property-type', 'bedrooms', 'bathrooms'];
      break;
    case (screenWidth > 767):
      hiddenFilters.value = ['property-type', 'bedrooms'];
      break;
    case (screenWidth > 491):
      hiddenFilters.value = [''];
      break;
    case (screenWidth < 491):
      hiddenFilters.value = [''];
      break;
  }
}
</script>

<style lang="scss">
.listing-search-filter-row {
  margin-top: 4px;
  height: 60px;
  background: #ffffff;
  max-width: 100% !important;
  z-index: 10 !important;
  position: sticky;

  .__filter-item-container {
    display: inline-flex;
    align-items: center;
    max-height: 60px;
    border-right: 1px solid rgba(var(--text-lighter-1), 0.3);
    position: relative;
    width: 100%;
    flex-direction: row;
    justify-content: space-evenly;

    // Search
    .__input-search {
      min-width: 360px;
      border-left: 1px solid rgba(var(--text-lighter-1), 0.3);
      height: 60px;
      margin-right: 12px;
    }

    // Dropdown items
    .__filter-dropdown {
      height: 60px;
      border-left: 1px solid rgba(var(--text-lighter-1), 0.3);
      padding-top: 5px;
      padding-bottom: 0;
      min-width: 120px;

      .__input-dropdown-inner {
        min-height: unset;
        border: none;
        padding-top: 2px;

        .__selection {
          color: rgb(var(--text-darken-1));
        }
      }

      .__dropdown-label {
        color: rgb(var(--text-lighter-1));
        margin-bottom: 0;
        font-weight: 400;
        padding: 5px 12px;
      }

      .__dropdown-items-wrapper {
        top: 55px;
      }

      &.__property-type {
        min-width: 185px;
      }
    }

    // Budget filter
    .__filter-budget {
      cursor: pointer;
      position: relative;
      border-left: 1px solid rgba(var(--text-lighter-1), 0.3);
      padding: 5px 12px 12px;
      min-width: 120px;
      height: 60px;
      margin-right: 12px;

      .__label {
        color: rgb(var(--text-lighter-1));
        margin-bottom: 0;
        font-weight: 400;
        font-size: 14px;
        padding: 5px 0;
        display: block;
      }

      .__budget-inner {
        display: inline-flex;
        margin-top: 3px;

        .__value {
          font-size: 16px;
          color: rgb(var(--text-darken-1));
        }

        img {
          position: absolute;
          right: 0;
          bottom: 15px;
          transform: rotate(0deg);
          transform-origin: center center;
          transition: all 0.2s ease-in;
        }
      }

      &.--opened {
        .__budget-inner {
          img {
            transform: rotate(180deg);
            transition: all 0.2s ease-in;
          }
        }
      }
    }

    .__filter-range-slider {
      max-height: 0;
      overflow: hidden;
      position: absolute;
      background: #fff;
      z-index: 999;
      border-bottom-right-radius: 8px;
      border-bottom-left-radius: 8px;
      box-shadow: 0 0 8px -2px #b4b4b4;
      min-width: 780px;
      max-width: 100%;
      width: 100%;
      right: -12px;
      top: 60px;

      .__budget-range {
        padding: 12px 12px 18px;
      }

      &.--opened {
        max-height: 200px;
        overflow: auto;
        transition: max-height 0.4s cubic-bezier(.79, .14, .15, .86);
      }
    }

    // Other search trigger
    .__btn-slider-icon {
      padding: 0 12px 0 15px;
      border-left: 1px solid rgba(var(--text-lighter-1), 0.3);
      height: 60px;
    }

    .blocks-other-search-popup {
      top: 60px;
      z-index: 999;
    }
  }

  // Responsive
  // -------------------------------------------------------------------------------------------------------------------
  @media (max-width: $grid-xl) {
    .container {
      max-width: unset;
      padding: 0;
      width: 100% !important;

      .__filter-item-container {
        .__input-search {
          min-width: 260px !important;
        }

        .__filter-dropdown {
          min-width: 120px;

          &.__property-type {
            min-width: 185px;
          }
        }
      }
    }
  }

  @media screen and (max-width: $grid-sm) {
    .__filter-item-container {
      .__input-text-inner {
        .__input-field-wrapper {
          .__input-label {
            padding-left: 8px;
          }

          input {
            margin-top: -6px;
          }

          .__prepend-icon-wrapper {
            bottom: 8px;
          }
        }
      }
    }
  }

  @media (max-width: 350px) {
    .container {
      .__filter-item-container {
        .__input-search {
          min-width: 255px;

          .__input-text-inner {
            .__input-field-wrapper {
              .__input-label {
                padding-left: 8px;
              }

              input {
                margin-top: -6px;
              }

              .__prepend-icon-wrapper {
                bottom: 8px;
              }
            }
          }
        }

        .__filter-dropdown {
          min-width: 120px;

          &.__property-type {
            min-width: 140px;
          }
        }
      }
    }
  }
}
</style>
